// src/lib/sppPreview.js
// ───────────────────────────────────────────────────────────────────────────
// 🪞 ESPEJO CLIENTE de computeSppMsf — SOLO para el PREVIEW EN VIVO del Configurador.
//
// El motor de precio vive en el servidor (functions/lib/pricing.js); la UI NO cotiza.
// Pero el Configurador necesita mostrar el $/MSF de un combo SPP contra la matriz EDITADA
// (aún sin guardar), antes de mandar el patch a la CF. Para eso porta la fórmula PURA aquí.
//
// ⚠️ ESTO ES UN ESPEJO, NO LA FUENTE. La VERDAD de precio (la matriz `sppMatrix`) sigue
// viviendo en `pricingConfig/default` y se lee/escribe SOLO por Cloud Function — esto solo
// recalcula visualmente. Si esta fórmula y functions/lib/pricing.js divergen, el parity test
// (src/lib/sppPreview.test.js, mismas 9 anclas que functions/scripts/verify-spp-matrix.mjs)
// se pone rojo. NO editar la matemática sin re-verificar ambos lados.
// ───────────────────────────────────────────────────────────────────────────

const SPP_DOUBLE_FLUTES = new Set(['BC', 'EB', 'BE']);

export function sppParedOf(flute) {
  return SPP_DOUBLE_FLUTES.has(String(flute || '').toUpperCase()) ? 'double' : 'single';
}

// Normaliza el `attributes.test` del doc ('200#','32ECT','44ECT'...) al grado SPP ('200','ECT32'...).
export function sppNormTest(test) {
  let t = String(test || '').trim().toUpperCase().replace(/#$/, '');
  const m = t.match(/^(\d+)ECT$/);
  if (m) t = `ECT${m[1]}`;
  return t;
}

// Grado SPP para el upcharge (sufijo SW/DW segun pared, como en la lista SPP).
export function sppGrade(normTest, pared) {
  if ((normTest === '275' || normTest === '350') && pared === 'double') return `${normTest}DW`;
  if (normTest === '275' && pared === 'single') return '275SW';
  return normTest;
}

// Upcharge $/MSF de UNA cara/liner. KRAFT=0. WHITE=White Top segun test. KEMI=adder Kemi si aplica.
export function sppLinerUpcharge(liner, normTest, pared, sm) {
  const L = String(liner || '').toUpperCase();
  if (L === 'KRAFT' || !L) return 0;
  const g = sppGrade(normTest, pared);
  if (L === 'KEMI') { const k = sm.upcharges && sm.upcharges.kemi; return (k && k.tests.includes(g)) ? k.perMsf : 0; }
  if (L === 'WHITE') {
    const wt = (sm.upcharges && sm.upcharges.whiteTop) || {};
    for (const grade of Object.keys(wt)) if (wt[grade].tests.includes(g)) return wt[grade].perMsf;
    return 0;
  }
  return 0;
}

/**
 * $/MSF SPP corrugado por COMPOSICION (PRE-waste; el carril aplica wasteFactor). PURO.
 * Espejo VERBATIM de functions/lib/pricing.js::computeSppMsf. Devuelve null si no compone.
 * @param {object} input { test, flute, outerLiner, innerLiner, sheetL, sheetW, sheets }
 * @param {object} sppMatrix { bases:{ 'test|pared':{msf:[5],flutes:[]} }, upcharges, msfBrackets, wasteFactor }
 * @return {number|null} $/MSF compuesto (2 decimales), o null si no es componible
 */
export function computeSppMsf(input, sppMatrix) {
  if (!sppMatrix || !sppMatrix.bases) return null;
  const { test, flute, outerLiner, innerLiner, sheetL, sheetW, sheets } = input || {};
  const fl = String(flute || '').toUpperCase();
  const pared = sppParedOf(fl);
  const nt = sppNormTest(test);
  const baseRow = sppMatrix.bases[`${nt}|${pared}`];
  if (!baseRow || !Array.isArray(baseRow.msf)) return null;
  if (pared === 'single' && Array.isArray(baseRow.flutes) && !baseRow.flutes.includes(fl)) return null;
  const totalMsf = ((Number(sheetL) || 0) * (Number(sheetW) || 0) / 144 / 1000) * (Number(sheets) || 0);
  const waste = sppMatrix.wasteFactor || 1;
  const brackets = sppMatrix.msfBrackets || [];
  const msf = totalMsf * waste;
  let idx = brackets.findIndex((b) => msf < b);
  if (idx === -1) idx = brackets.length;
  idx = Math.min(idx, baseRow.msf.length - 1);
  const base = baseRow.msf[idx];
  const flu = (sppMatrix.upcharges && sppMatrix.upcharges.flute && sppMatrix.upcharges.flute[fl]) || 0;
  const lin = sppLinerUpcharge(outerLiner, nt, pared, sppMatrix) + sppLinerUpcharge(innerLiner, nt, pared, sppMatrix);
  return Math.round((base + flu + lin) * 100) / 100;
}
